import { Track } from "@/src/lib/tracking";
import type { ChecklistStepKey, ChecklistEntrySource } from "../types";

/** 最近一次点击清单动作按钮的入口（sheet / card_signal / card_sidebar），供后续流程取 entry */
let lastActionEntry: ChecklistEntrySource | null = null;

/**
 * 起步清单埋点门面 — fe-spec §3.4
 *
 * 只走 `Track.*`（OpenReplay 单通道，AGENTS「业务埋点只走 OpenReplay」/ tracking-conventions §0），
 * 事件名与 KMP 端逐字一致。`*_view` 是曝光 → `Track.view`；其余是动作/结果 → `Track.event`。
 */
// ═══ 入口与起步清单主漏斗埋点（PRD S1.1-S1.3, S1.13-S1.14）═══

/** 三个入口各自曝光（entry = topleft / card_signal / card_sidebar） */
export const trackEntryView = (
  entry: ChecklistEntrySource | "card_signal" | "card_sidebar",
) => Track.view("onboard_entry_view", { entry });

/** 点击左上角 Setup with Ed */
export const trackTopleftTap = () => Track.event("onboard_topleft_tap");

/** 单件卡曝光 */
export const trackCardView = (
  item: ChecklistStepKey,
  entry: "card_signal" | "card_sidebar",
) => Track.view("onboard_card_view", { item, entry });

/** 点击单件卡 All setup */
export const trackCardAllSetupTap = (entry: "card_signal" | "card_sidebar") =>
  Track.event("onboard_card_all_setup_tap", { entry });

/** 左右滑走信号位 setup 卡 */
export const trackCardSwipeDismiss = (item: ChecklistStepKey) =>
  Track.event("onboard_card_swipe_dismiss", { item });

/** 清单 Sheet 曝光 */
export const trackSheetView = (
  entry: ChecklistEntrySource,
  itemsDone: number,
) => Track.view("onboard_sheet_view", { entry, items_done: itemsDone });

/** 手动点击手风琴展开某项 */
export const trackItemExpand = (item: ChecklistStepKey) =>
  Track.event("onboard_item_expand", { item });

/**
 * 点击某项动作按钮。
 * 同时记下入口：后续的系统通知授权等流程要带上 entry（sheet / card），见 `getLastActionEntry`
 */
export const trackItemActionTap = (
  item: ChecklistStepKey,
  entry: ChecklistEntrySource,
) => {
  lastActionEntry = entry;
  Track.event("onboard_item_action_tap", { item, entry });
};

/** 某项由未完成变为已完成 */
export const trackItemDone = (item: ChecklistStepKey, itemsDone: number) =>
  Track.event("onboard_item_done", { item, items_done: itemsDone });

/** 完成状态读取失败展示 error 态 */
export const trackSheetErrorView = (errorMsg: string) =>
  Track.view("onboard_sheet_error_view", { error_msg: errorMsg });

/** 6/6 完成庆祝页曝光 */
export const trackSetupCompleteView = () => Track.view("setup_complete_view");

/** 庆祝页点击 Got it 或关闭 */
export const trackSetupCompleteDismiss = () =>
  Track.event("setup_complete_dismiss");

// ═══ 项 1 · 财务健康分（PRD S1.4）═══

/** 财务健康分页曝光 */
export const trackFfScorePageView = () => Track.view("ff_score_page_view");

/** 出分成功 */
export const trackFfScoreSuccess = (score: number | null | undefined) =>
  Track.event("ff_score_success", { score });

/** 出分失败展示 error 态 */
export const trackFfScoreErrorView = () => Track.view("ff_score_error_view");

// ═══ 项 2 · 打开通知（PRD S1.5-S1.7）═══

/** 通知相关事件的进入点：sheet / 单件卡 / 首读等待弹窗 */
export type PushEntry = "sheet" | "card" | "first_read";

/** 把最近一次动作入口折算成通知事件的 entry；取不到时按 sheet */
export const getLastPushEntry = (): PushEntry => {
  if (lastActionEntry === "card_signal" || lastActionEntry === "card_sidebar") {
    return "card";
  }
  return "sheet";
};

/** 调起系统授权弹窗 */
export const trackPushSystemPromptView = (entry: PushEntry) =>
  Track.view("push_system_prompt_view", { entry });

/**
 * 授权成功：系统弹窗中允许（fromSettings=false），或从系统设置页返回后读到已授权（fromSettings=true）。
 *
 * 与 App 原生推送引导共用 `push_permission_granted` 这个事件名，而原生的 `source` 键表示「时机 5 的入口」
 * （brief_card / chat），语义不同，所以这里不用 `source=prompt/settings`，改用原生已有的 `from_settings`
 * 布尔键表示是否来自设置页，再用 `entry` 区分 web 清单的进入点（原生不带 entry）。
 */
export const trackPushPermissionGranted = (
  entry: PushEntry,
  fromSettings: boolean,
) =>
  Track.event("push_permission_granted", {
    entry,
    from_settings: fromSettings,
  });

/** 系统弹窗中拒绝 */
export const trackPushPermissionDenied = (entry: PushEntry) =>
  Track.event("push_permission_denied", { entry });

/** 因已拒绝态跳系统设置页 */
export const trackPushSettingsRedirect = (entry: PushEntry) =>
  Track.event("push_settings_redirect", { entry });

/** 从系统设置页返回但仍未授权 */
export const trackPushSettingsAbandon = (entry: PushEntry) =>
  Track.event("push_settings_abandon", { entry });

// ═══ 项 3 / 4 · 连接账户（PRD S1.8）═══

export type ConnectAccountType = "invest" | "banking";

/** 连账户页曝光 */
export const trackConnectPageView = (accountType: ConnectAccountType) =>
  Track.view("connect_page_view", { account_type: accountType });

/** Plaid 授权成功 */
export const trackPlaidConnectSuccess = (accountType: ConnectAccountType) =>
  Track.event("plaid_connect_success", { account_type: accountType });

/** Plaid 授权失败或中断 */
export const trackPlaidConnectError = (accountType: ConnectAccountType) =>
  Track.event("plaid_connect_error", { account_type: accountType });

/** 首读等待弹窗曝光 */
export const trackFirstReadWaitView = (accountType: ConnectAccountType) =>
  Track.view("first_read_wait_view", { account_type: accountType });

/** 首读等待弹窗内点 Turn on notifications */
export const trackFirstReadPushTap = () => Track.event("first_read_push_tap");

// ═══ 项 5 · 建 tracker（PRD S1.9）═══

/** 清单方向选择页选中的方向：用来给之后 chat 里创建成功的 tracker 带上 direction（30 分钟内有效） */
const TRACKER_DIRECTION_TTL_MS = 30 * 60 * 1000;
let pendingTrackerDirection: { direction: string; at: number } | null = null;

export const rememberTrackerDirection = (direction: string): void => {
  pendingTrackerDirection = { direction, at: Date.now() };
};

/** 取走（并清除）最近选中的方向；没有或已过期返回 undefined */
export const consumeTrackerDirection = (): string | undefined => {
  const pending = pendingTrackerDirection;
  pendingTrackerDirection = null;
  if (!pending || Date.now() - pending.at > TRACKER_DIRECTION_TTL_MS) {
    return undefined;
  }
  return pending.direction;
};

/** 方向选择页曝光 */
export const trackTrackerEntryView = () => Track.view("tracker_entry_view");

/** 点某方向卡 */
export const trackTrackerDirectionTap = (direction: string) =>
  Track.event("tracker_direction_tap", { direction });

/** 经 chat 创建 tracker 成功；direction 为用户在清单方向选择页选中的方向（没经过该页则缺省） */
export const trackTrackerCreateSuccess = (direction?: string) =>
  Track.event("tracker_create_success", { direction, path: "chat" });

// ═══ 项 6 · 激活目标（PRD S1.10-S1.11）═══

export type GoalActivateEntryState =
  "refined" | "unrefined" | "multiple" | "none";

/** 激活承接页曝光；多个待激活 goal 的选择页同时带 pending_count */
export const trackGoalActivateEntryView = (
  state: GoalActivateEntryState,
  pendingCount?: number,
) =>
  Track.view("goal_activate_entry_view", {
    state,
    pending_count: pendingCount,
  });

// 选中某个 goal（goal_select_tap）由选择页卡片的声明式 data-or-event 上报，不在这里手写
/** chat 中立起 goal 成功 */
export const trackGoalCreateSuccess = () => Track.event("goal_create_success");
